/**
 * F design-system recipes - the canonical sx objects extracted from the
 * public pages (the gold standard). Internal pages consume these via
 * useRecipes(); public pages keep their proven inline copies until the
 * optional de-duplication phase.
 *
 * Sources of truth (copied, not invented):
 *   PublicCommodities.jsx  cardSx/tsSx/statLabelSx/ghostIconSx/catChipSx/
 *                          rangeBtnSx/toggleGroupSx/paginationSx/chart
 *   PublicMarkets.jsx      controlSx/labelSx/menuProps/checkboxSx/searchSx/
 *                          filterChipSx/stickyStripSx/headCellSx/sortLabelSx/
 *                          dialogPaperProps/tableFooterSx/emptyBoxSx
 *   PublicInsights.jsx     primaryBtnSx
 */

import { useMemo } from 'react';
import { radius, MONO, numericSx, useThemeMode } from '../context/ThemeContext';

export function makeRecipes(palette) {
  const cardSx = {
    bgcolor: palette.card,
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: `${radius.card}px`,
    boxShadow: palette.shadowSm,
  };

  const chart = {
    gridStyle: { stroke: palette.chartGrid, strokeDasharray: '3 3', vertical: false },
    axisStyle: { tick: { fontSize: 11, fill: palette.chartAxis, fontFamily: MONO }, axisLine: false, tickLine: false },
    // Palette-driven series ramp (replaces the hardcoded chartColors hexes).
    series: [palette.accent, palette.navy, palette.info, palette.warning, palette.danger, palette.teal, palette.purple, palette.brass],
    tooltip: {
      contentStyle: {
        backgroundColor: palette.tooltipBg,
        border: `1px solid ${palette.tooltipBorder}`,
        borderRadius: 10,
        fontSize: '0.8rem',
        padding: '10px 14px',
        color: palette.tooltipText,
      },
      labelStyle: { fontWeight: 600, color: palette.accentLight, marginBottom: 4 },
      itemStyle: { color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem' },
    },
  };

  return {
    cardSx,

    // 30px rounded-square icon tile (benchmark card anatomy)
    iconTileSx: {
      width: 30,
      height: 30,
      borderRadius: '8px',
      flexShrink: 0,
      bgcolor: palette.surface,
      border: `1px solid ${palette.cardBorder}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    // italic "Updated · date" stamp
    tsSx: { fontSize: '0.62rem', color: palette.textMuted, fontStyle: 'italic', letterSpacing: '0.02em' },

    // uppercase micro-label above stats
    statLabelSx: { fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: palette.textMuted, mb: 0.5 },

    focusRingSx: { outline: `2px solid ${palette.accent}`, outlineOffset: 2 },

    // ---- filter strip ----
    controlSx: {
      bgcolor: palette.card,
      borderRadius: `${radius.control}px`,
      fontSize: '0.86rem',
      color: palette.text,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.cardBorder },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.textMuted },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.accent, boxShadow: `0 0 0 3px ${palette.accentBg}` },
      '& .MuiSvgIcon-root': { color: palette.textMuted },
    },
    labelSx: { color: palette.textSecondary, fontWeight: 600, '&.Mui-focused': { color: palette.accent } },
    menuProps: {
      PaperProps: {
        sx: {
          maxHeight: 340,
          mt: 0.5,
          borderRadius: `${radius.control}px`,
          border: `1px solid ${palette.cardBorder}`,
          boxShadow: palette.shadowLg,
          '& .MuiMenuItem-root': { py: 0.25 },
        },
      },
    },
    checkboxSx: { color: palette.textMuted, '&.Mui-checked': { color: palette.accent } },
    searchSx: {
      '& .MuiOutlinedInput-root': {
        borderRadius: `${radius.control}px`,
        bgcolor: palette.card,
        color: palette.text,
        fontSize: '0.86rem',
        '& fieldset': { borderColor: palette.cardBorder },
        '&:hover fieldset': { borderColor: palette.textMuted },
        '&.Mui-focused fieldset': { borderColor: palette.accent, boxShadow: `0 0 0 3px ${palette.accentBg}` },
      },
      '& .MuiInputBase-input::placeholder': { color: palette.textMuted, opacity: 1 },
    },
    filterChipSx: {
      fontSize: '0.75rem',
      fontWeight: 600,
      height: 26,
      bgcolor: palette.surface,
      color: palette.text,
      border: `1px solid ${palette.cardBorder}`,
      '& .MuiChip-deleteIcon': { color: palette.textMuted, fontSize: 16, '&:hover': { color: palette.danger } },
    },
    stickyStripSx: {
      position: 'sticky',
      top: 64,
      zIndex: 10,
      background: palette.navBg,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${palette.navBorder}`,
      py: 2,
    },

    // ---- buttons and chips ----
    toggleGroupSx: {
      '& .MuiToggleButton-root': {
        fontSize: '0.72rem',
        fontWeight: 600,
        textTransform: 'none',
        color: palette.textSecondary,
        borderColor: palette.cardBorder,
        '&.Mui-selected': { bgcolor: palette.accentBg, color: palette.accent, '&:hover': { bgcolor: palette.accentBg } },
      },
    },
    rangeBtnSx: (active) => ({
      minWidth: 40,
      px: 1.1,
      py: 0.35,
      fontFamily: MONO,
      fontSize: '0.7rem',
      fontWeight: 600,
      color: active ? '#fff' : palette.textSecondary,
      bgcolor: active ? palette.accent : 'transparent',
      borderColor: palette.cardBorder,
      '&:hover': { bgcolor: active ? palette.accent : palette.surfaceHover, borderColor: active ? palette.accent : palette.textMuted },
    }),
    ghostIconSx: {
      width: 34,
      height: 34,
      borderRadius: `${radius.control}px`,
      border: `1px solid ${palette.cardBorder}`,
      bgcolor: palette.card,
      color: palette.textSecondary,
      '&:hover': { bgcolor: palette.surface, borderColor: palette.textMuted, color: palette.text },
    },
    catChipSx: (active) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      userSelect: 'none',
      fontWeight: 600,
      fontSize: '0.8rem',
      borderRadius: 999,
      px: 1.6,
      py: 0.7,
      bgcolor: active ? palette.accent : palette.card,
      color: active ? '#fff' : palette.textSecondary,
      border: `1px solid ${active ? palette.accent : palette.cardBorder}`,
      transition: 'background-color .2s, border-color .2s, color .2s',
      '&:hover': { borderColor: active ? palette.accent : palette.textMuted, bgcolor: active ? palette.accent : palette.surface },
    }),
    primaryBtnSx: {
      bgcolor: palette.accent,
      color: '#fff',
      fontWeight: 600,
      px: 3.25,
      py: 1.35,
      fontSize: '0.95rem',
      borderRadius: '10px',
      whiteSpace: 'nowrap',
      '&:hover': { bgcolor: '#1C9550', boxShadow: palette.shadowMd },
    },

    // ---- tables ----
    paginationSx: {
      color: palette.textSecondary,
      '& .MuiTablePagination-toolbar': { minHeight: 44, pl: 0 },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { ...numericSx, fontSize: '0.78rem', color: palette.textSecondary },
      '& .MuiTablePagination-select': { ...numericSx, fontSize: '0.78rem' },
      '& .MuiSvgIcon-root': { color: palette.textMuted },
    },
    headCellSx: {
      fontWeight: 700,
      fontSize: '0.68rem',
      color: palette.textMuted,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      bgcolor: palette.surface,
      borderBottom: `1px solid ${palette.divider}`,
      py: 1.4,
      whiteSpace: 'nowrap',
    },
    sortLabelSx: {
      color: `${palette.textMuted} !important`,
      '&:hover': { color: `${palette.text} !important` },
      '&.Mui-active': { color: `${palette.text} !important` },
      '&.Mui-active .MuiTableSortLabel-icon': { color: `${palette.accent} !important` },
    },
    // hover-tint body rows (the F system has no zebra striping)
    bodyRowSx: {
      '&:hover': { bgcolor: palette.cardHover },
      '& td': { borderBottom: `1px solid ${palette.divider}`, py: 1.3, fontSize: '0.85rem' },
    },
    tableFooterSx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 1,
      px: 2.5,
      py: 1,
      bgcolor: palette.surface,
      borderTop: `1px solid ${palette.divider}`,
    },

    // ---- tabs ----
    tabsSx: {
      '& .MuiTab-root': { fontSize: '0.9rem', color: palette.textSecondary, minHeight: 48 },
      '& .Mui-selected': { color: palette.accent },
      '& .MuiTabs-indicator': { backgroundColor: palette.accent },
    },

    // ---- empty + dialog ----
    emptyBoxSx: {
      py: 6,
      px: 3,
      textAlign: 'center',
      border: `1px dashed ${palette.cardBorder}`,
      borderRadius: `${radius.control}px`,
      bgcolor: palette.surface,
    },
    dialogPaperProps: {
      sx: {
        borderRadius: `${radius.panel}px`,
        bgcolor: palette.card,
        border: `1px solid ${palette.cardBorder}`,
        boxShadow: palette.shadowLg,
        maxWidth: 420,
        m: 2,
      },
    },

    chart,
  };
}

export function useRecipes() {
  const { palette } = useThemeMode();
  return useMemo(() => makeRecipes(palette), [palette]);
}
